import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { FormSelect } from '../../components/finance';
import { CheckCircle, XCircle, Clock, CircleNotch, ArrowLeft, Question } from '@phosphor-icons/react';
import './transport.css';

const TRIP_TYPE_OPTIONS = [
  { value: 'pickup', label: 'Pickup' },
  { value: 'dropoff', label: 'Dropoff' }
];

function AttendanceTrackingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [tripType, setTripType] = useState('pickup');
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('mark'); // 'mark' or 'view'
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const data = await transportService.listRoutes({ status: 'active' });
        const routesList = Array.isArray(data) ? data : data.routes || [];
        setRoutes(routesList);
        if (routesList.length > 0) {
          setSelectedRoute(routesList[0].id);
        }
      } catch (error) {
        toast.error('Failed to load routes');
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, []);

  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedRoute) return;
      try {
        const data = await transportService.getRouteStudents(selectedRoute);
        console.log('Fetched route students:', data);
        const studentList = Array.isArray(data) ? data : data.students || [];
        setStudents(studentList);
        const initialAttendance = {};
        studentList.forEach(student => {
          initialAttendance[student.id] = 'present';
        });
        setAttendance(initialAttendance);
      } catch (error) {
        toast.error('Failed to load students');
      }
    };

    loadStudents();
  }, [selectedRoute]);

  const loadAttendanceRecords = React.useCallback(async () => {
    if (!selectedRoute) {
      toast.error('Please select a route');
      return;
    }

    try {
      setLoadingRecords(true);
      
      // Fetch attendance records
      const data = await transportService.getRouteAttendance(selectedRoute, {
        attendance_date: selectedDate, trip_type: tripType
      });
      console.log('Fetched attendance records:', data);
      setAttendanceRecords(Array.isArray(data) ? data : data.records || []);
    } catch (error) {
      toast.error('Failed to load attendance records: ' + error.message);
    } finally {
      setLoadingRecords(false);
    }
  }, [selectedRoute, selectedDate, tripType]);

  useEffect(() => {
    if (viewMode === 'view') {
      loadAttendanceRecords();
    }
  }, [viewMode, selectedRoute, selectedDate, tripType, loadAttendanceRecords]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleBulkMark = (status) => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = status;
    });
    setAttendance(newAttendance);
    toast.success(`Marked all as ${status}`);
  };

  const handleSubmit = async () => {
    if (!selectedRoute) {
      toast.error('Please select a route');
      return;
    }

    setSubmitting(true);
    try {
      const routeVehicleId = routes.find(r => r.id === selectedRoute)?.vehicle_id || '';
      const records = students.map(student => ({
        route_id: selectedRoute,
        student_id: student.id,
        vehicle_id: routeVehicleId,
        attendance_date: selectedDate,
        status: attendance[student.id] || 'present',
        trip_type: tripType,
        timestamp: new Date().toISOString()
      }));

      await transportService.bulkMarkAttendance(records);
      toast.success(`Attendance marked for ${students.length} students`);
      setAttendance({});
    } catch (error) {
      toast.error('Failed to submit attendance: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading routes...</div></Layout>;
  }

  const routeOptions = routes.map(route => ({
    value: route.id,
    label: `${route.route_name} (${route.route_code})`
  }));

  return (
    <Layout>
      <div className="page attendance-tracking">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Transport Attendance</h1>
              <p>Mark and track student attendance for routes</p>
            </div>
          </div>
          <div className="view-toggle">
            <button 
              className={`btn ${viewMode === 'mark' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('mark')}
            >
              Mark Attendance
            </button>
            <button 
              className={`btn ${viewMode === 'view' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('view')}
            >
              View Records
            </button>
          </div>
        </div>

        <div className="filters-section">
          <FormSelect
            label="Select Route"
            value={selectedRoute}
            options={routeOptions}
            onChange={(e) => setSelectedRoute(e.target.value)}
            placeholder="Select Route"
          />

          <div className="form-group">
            <label>Date</label>
            <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
          </div>

          <FormSelect
            label="Trip Type"
            value={tripType}
            options={TRIP_TYPE_OPTIONS}
            onChange={(e) => setTripType(e.target.value)}
          />
        </div>

        {viewMode === 'mark' ? (
          <>
        <div className="bulk-actions">
          <button className="btn btn-success" onClick={() => handleBulkMark('present')}>
            <CheckCircle size={16} /> Mark All Present
          </button>
          <button className="btn btn-danger" onClick={() => handleBulkMark('absent')}>
            <XCircle size={16} /> Mark All Absent
          </button>
          <button className="btn btn-secondary" onClick={() => handleBulkMark('late')}>
            <Question size={16} /> Mark All Late
          </button>
        </div>

        <div className="attendance-grid">
          {students.length === 0 ? (
            <div className="empty-state">
              <p>No students enrolled on this route</p>
            </div>
          ) : (
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Student ID</th>
                  <th>Pickup Point</th>
                  <th style={{ minWidth: 280 }}>Attendance Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.id}>
                    <td>{student.first_name} {student.last_name}</td>
                    <td>{student.student_id}</td>
                    <td>{student.pickup_point || '-'}</td>
                    <td>
                      <div className="status-buttons">
                        <button
                          className={`status-btn present ${attendance[student.id] === 'present' ? 'active' : ''}`}
                          onClick={() => handleAttendanceChange(student.id, 'present')}
                        >
                          <CheckCircle size={16} /> Present
                        </button>
                        <button
                          className={`status-btn absent ${attendance[student.id] === 'absent' ? 'active' : ''}`}
                          onClick={() => handleAttendanceChange(student.id, 'absent')}
                        >
                          <XCircle size={16} /> Absent
                        </button>
                        <button
                          className={`status-btn late ${attendance[student.id] === 'late' ? 'active' : ''}`}
                          onClick={() => handleAttendanceChange(student.id, 'late')}
                        >
                          <Clock size={16} /> Late
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="submit-section">
          <button
            className="btn btn-primary btn-lg"
            onClick={handleSubmit}
            disabled={submitting || students.length === 0}
          >
            {submitting ? (
              <span className="button-loading">
                <CircleNotch size={16} weight="fill" className="spinner" />
                Submitting...
              </span>
            ) : (
              `Submit Attendance (${students.length} students)`
            )}
          </button>
        </div>
          </>
        ) : (
          <div className="attendance-view">
            {loadingRecords ? (
              <div className="loading">Loading attendance records...</div>
            ) : attendanceRecords.length === 0 ? (
              <div className="empty-state">
                <p>No attendance records found for this route and date</p>
              </div>
            ) : (
              <>
                <div className="records-summary">
                  <div className="summary-card">
                    <h3>Total Marked</h3>
                    <p className="summary-number">{attendanceRecords.length}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Present</h3>
                    <p className="summary-number present">{attendanceRecords.filter(r => r.status === 'present').length}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Absent</h3>
                    <p className="summary-number absent">{attendanceRecords.filter(r => r.status === 'absent').length}</p>
                  </div>
                  <div className="summary-card">
                    <h3>Late</h3>
                    <p className="summary-number late">{attendanceRecords.filter(r => r.status === 'late').length}</p>
                  </div>
                </div>

                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Student ID</th>
                      <th>Status</th>
                      <th>Trip Type</th>
                      <th>Date</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRecords.map(record => (
                      <tr key={record.id}>
                        <td><strong>{record.first_name} {record.last_name}</strong></td>
                        <td>{record.student_id}</td>
                        <td><span className={`badge badge-${record.status}`}>{record.status}</span></td>
                        <td>{record.trip_type}</td>
                        <td>{record.attendance_date}</td>
                        <td>{record.timestamp ? new Date(record.timestamp).toLocaleTimeString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AttendanceTrackingPage;
